import Moment from 'moment';

/**
 * This function is used to sort text strings alphabetically.
 *
 * @param {string} dateA - A date embedded in a str., '07-16-2021'
 * @param {string} dateB - A date embedded in a str., '06-15-2021'
 *
 * @return {number} A number signifying whether the elements are =, <, or > each other.
 *
 * @example
 *
 *     .soft(sortTextDates)
 *     sortTextDates('07-16-2021', '06-15-2021')
 */
export function sortTextDates(dateA, dateB) {
  const dateFormat = 'MMDDYYYY';

  return new Moment(dateA).format(dateFormat) - new Moment(dateB).format(dateFormat);
}

/**
 * This is the ternary expression that is used for the actual sorting in sort().
 * If the .sort() method needs no special tinkering, this can be used directly in .sort().
 *
 * @param {(string|object)} a - The first param of the array to be sorted.
 * @param {(string|object)} b - The second param of the array to be sorted.
 *
 * @return {number} A number signifying whether the elements are =, <, or > each other.
 *
 * @example
 *
 *     sortReturnTernary(toLower(titleA), toLower(titleB));
 */
export function sortReturnTernary(a, b) {
  return a === b
    ? 0
    : a < b
      ? -1
      : 1;
}

/**
 * This function is used to sort text strings alphabetically.
 *
 * @param {string} textA - Some text
 * @param {string} textB - some other text
 *
 * @return {number} A number signifying whether the text elements are =, <, or > each other.
 *
 * @example
 *
 *     .soft(sortTextAlphabetically)
 *     sortTextAlphabetically('cat', 'DOG')
 */
export function sortTextAlphabetically(textA, textB) {
  return sortReturnTernary(textA.toLowerCase(), textB.toLowerCase());
}

/**
 * This sorts a series of cards by their title property.
 *
 * @param {(string|object)} classA - A class object, to potentially be 1 displayed card.
 * @param {(string|object)} classB - A class object, to potentially be 1 displayed card.
 *
 * @return {number} A number signifying whether the elements are =, <, or > each other.
 *
 * @example
 *
 *     .soft(sortAlphabeticallyByTitleProp)
 */
export function sortAlphabeticallyByTitleProp(classA, classB) {
  const { title: titleA } = classA;
  const { title: titleB } = classB;

  return sortTextAlphabetically(titleA, titleB);
}

/**
 * This sorts a series of cards by their date property.
 *
 * @param {(string|object)} classA - A class object, to potentially be 1 displayed card.
 * @param {(string|object)} classB - A class object, to potentially be 1 displayed card.
 *
 * @return {number} A number signifying whether the elements are =, <, or > each other.
 *
 * @example
 *
 *     .soft(sortByStartDateProp)
 */
export function sortByStartDateProp(classA, classB) {
  const { startDate: startDateA } = classA;
  const { startDate: startDateB } = classB;
  const unixTimestampFormat = 'X';
  const formatDateForSorting = (date) => new Moment(date, 'MM-DD-YYYY').format(unixTimestampFormat);

  return formatDateForSorting(startDateA) - formatDateForSorting(startDateB);
}

export default {
  sortReturnTernary,
  sortAlphabeticallyByTitleProp,
  sortByStartDateProp,
  sortTextDates,
  sortTextAlphabetically,
};
